import React, { useEffect, useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import moment from 'moment';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyMember = { employee_id: '', name: '', email: '', membership_end_date: '' };

const ErrorText = ({ message }) =>
  message ? <p style={{ color: 'red', fontStyle: 'Italic', marginBottom: 0 }}>{message}</p> : null;

// Add (member = null) or edit an existing member.
const MemberFormModal = ({ isOpen, member, groupEndDate, saving, onClose, onSubmit }) => {
  const isEdit = !!member;
  const [values, setValues] = useState(emptyMember);
  const [errors, setErrors] = useState({});
  const today = moment().format('YYYY-MM-DD');

  useEffect(() => {
    if (isOpen) {
      setValues(
        member
          ? {
              employee_id: member.employee_id || '',
              name: member.name || '',
              email: member.email || '',
              membership_end_date: member.membership_end_date || ''
            }
          : emptyMember
      );
      setErrors({});
    }
  }, [isOpen, member]);

  const handleChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!values.employee_id.trim()) errors.employee_id = 'Employee ID is required!';
    if (!values.name.trim()) errors.name = 'Name is required!';
    if (!values.email.trim()) {
      errors.email = 'Email is required!';
    } else if (!EMAIL_REGEX.test(values.email.trim())) {
      errors.email = 'Enter a valid email address!';
    }
    if (values.membership_end_date) {
      if (values.membership_end_date < today) {
        errors.membership_end_date = "End date can't be in the past!";
      } else if (groupEndDate && values.membership_end_date > groupEndDate) {
        errors.membership_end_date = `End date can't be after the group end date (${moment(groupEndDate).format('DD MMM YYYY')})!`;
      }
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      employee_id: values.employee_id.trim(),
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      membership_end_date: values.membership_end_date || null
    });
  };

  const emailChanged = isEdit && values.email.trim().toLowerCase() !== (member.email || '').toLowerCase();

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} className="modal-dialog-centered">
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={saving ? undefined : onClose}>{isEdit ? 'Edit Member' : 'Add Member'}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Row>
              <Col sm={6} className="mt-2">
                <Label>Employee ID*</Label>
                <Input value={values.employee_id} onChange={({ target }) => handleChange('employee_id', target.value)} />
                <ErrorText message={errors.employee_id} />
              </Col>
              <Col sm={6} className="mt-2">
                <Label>Name*</Label>
                <Input value={values.name} onChange={({ target }) => handleChange('name', target.value)} />
                <ErrorText message={errors.name} />
              </Col>
              <Col sm={12} className="mt-3">
                <Label>Email*</Label>
                <Input type="email" value={values.email} onChange={({ target }) => handleChange('email', target.value)} />
                <ErrorText message={errors.email} />
                {emailChanged ? (
                  <small className="text-500">
                    The member's app account moves to this email, so their watch history is kept. Both addresses will be notified.
                  </small>
                ) : (
                  !isEdit && <small className="text-500">A welcome email with access instructions will be sent here.</small>
                )}
              </Col>
              <Col sm={6} className="mt-3">
                <Label>Membership End Date</Label>
                <Input
                  type="date"
                  min={today}
                  max={groupEndDate || undefined}
                  value={values.membership_end_date}
                  onChange={({ target }) => handleChange('membership_end_date', target.value)}
                />
                <ErrorText message={errors.membership_end_date} />
                <small className="text-500">Leave blank to follow the group's validity.</small>
              </Col>
            </Row>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save' : 'Add Member'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default MemberFormModal;
